import { HTMLAttributes, useEffect } from 'react'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Media from '@/components/base/media'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { useLoaderStore } from '@/store/loader'

import { useHeroMotion } from './motion'
import { cn } from '@/lib/utils'
import { WPMedia } from '@/types'

export type HeroProps = HTMLAttributes<HTMLElement> &
  Partial<{
    pretitle: string
    title: string
    text: string
    media: WPMedia
  }>

const Hero = ({ pretitle, title, text, media }: HeroProps) => {
  const { motionRef } = useHeroMotion()
  const setLoaderState = useLoaderStore((state) => state.setState)

  const handleLoad = () => {
    setLoaderState({ pageLoaded: true, loaded: true })
  }

  useEffect(() => {
    if (
      !media?.poster?.url &&
      !media?.video?.url &&
      !media?.poster_mobile?.url &&
      !media?.video_mobile?.url
    ) {
      setLoaderState({ pageLoaded: true, loaded: true })
    }
  }, [media, setLoaderState])

  return (
    <Section ref={motionRef} className="relative">
      <div className="grid h-screen w-full">
        <div
          className={cn('col-[1/-1] row-[1/-1]', 'relative overflow-clip')}
          data-motion-media-mask
        >
          <div
            className={cn(
              'absolute left-0 top-0 z-10 h-full w-full',
              'bg-[white]/20 dark:bg-[black]/20'
            )}
          />
          {(media?.poster?.url || media?.video?.url) && (
            <Media onLoad={handleLoad} media={media} data-motion-media />
          )}
        </div>

        <Container className={cn('col-[1/-1] row-[1/-1]', 'h-svh-vh relative z-10')}>
          <Grid className="-mt-spacing-sm h-full items-center">
            <div
              className={cn(
                'col-span-2 md:col-span-5 lg:col-span-8 lg:col-start-3',
                'text-text-accent-light dark:text-text-primary-invert-light',
                'grid gap-y-spacing-md'
              )}
            >
              {pretitle ? (
                <Typography variant="body" data-motion-pretitle>
                  {pretitle}
                </Typography>
              ) : null}

              {title ? (
                <Typography variant="h5" component="h1" data-motion-title>
                  {title}
                </Typography>
              ) : null}

              {text ? (
                <Typography variant="body" data-motion-paragraph>
                  {text}
                </Typography>
              ) : null}
            </div>
          </Grid>
        </Container>
      </div>
    </Section>
  )
}

export default Hero
